import { buildReportCsv, csvFileName, downloadCsv } from "./csv";
import {
  announce,
  barTip,
  blankSpans,
  el,
  liveRegion,
  positionTally,
  replaceChildrenWith,
  untimedSpans
} from "./dom";
import { openExportDialog } from "./exportDialog";
import {
  channelKey,
  channelLabel,
  duration,
  integer,
  languageName,
  outcomeKey,
  outcomeLabel,
  positionLabel,
  resolveZone,
  stampInZone
} from "./format";
import { openProfile } from "./profile";
import { renderRoster } from "./roster";
import { turnIndexFor } from "./speakingTurns";
import { channelCoverageNote, timelineSections } from "./timelineView";
import type {
  AgendaItem,
  BreakdownSlice,
  ReportContext,
  SpeakerParticipationReport,
  SpeakerRecord,
  TimeProfile,
  Timeline
} from "./types";

/**
 * AB#9981. Renders the report document.
 *
 * The shape is the prototype's, and so is the reading order: what happened, does it
 * add up, then who, then the cross-tabs. What is different is that every number here
 * came from the server and every optional column can be absent.
 */

/** Beyond this many rows per item the roster states what it left out. */
const ROSTER_RENDER_CAP = 250;

export interface RenderOptions {
  root: HTMLElement;
  context: ReportContext;
  report: SpeakerParticipationReport;
  /** Posts an export request. Present only when the caller may export. */
  requestExport: ((recipientEmail: string) => Promise<void>) | null;
}

export function renderReport(options: RenderOptions): void {
  const { root, context, report } = options;
  const body = root.querySelector<HTMLElement>("[data-spr-body]");
  if (!body) return;

  const exportLive = liveRegion("speaker-report-export-live");
  const copyLive = liveRegion("speaker-report-copy-live");

  const blocks: Array<Node | null> = [
    viewBar(options, exportLive),
    // Dashboards lead. They are the board's own published views of this meeting, and a
    // reader who came for one should not have to scroll past the whole record to find it.
    dashboards(report),
    reconciliation(report),
    agenda(report, root),
    directory(report, root, context),
    breakdowns(report),
    timeProfile(report),
    // Two async effects, two regions. Sharing one node means the second
    // announcement silently overwrites the first.
    exportLive,
    copyLive
  ];

  replaceChildrenWith(body, blocks);
  root.setAttribute("data-loaded", "true");

  const skeleton = root.querySelector("[data-testid='speaker-report-skeleton']");
  skeleton?.parentNode?.removeChild(skeleton);
}

// -- chrome -----------------------------------------------------------------

/**
 * The view bar as it appears BEFORE the payload arrives.
 *
 * Same element structure and the same classes as viewBar, so it occupies exactly the same
 * height. AC-12 requires that content arrival not shift the header, and the header sits
 * directly below this bar: with nothing here during loading the whole document jumped by
 * the bar's height the moment the data landed.
 *
 * Deliberately adjacent to viewBar in this file. A guessed min-height in the stylesheet
 * would drift the first time the bar gains a control; sharing the structure cannot.
 */
export function viewBarSkeleton(context?: ReportContext): HTMLElement {
  // The two EXPORT placeholders are drawn only for a reader who will actually get them.
  //
  // viewBar() below returns early for an anonymous reader, on the stated grounds that an
  // export control on a shared link advertises that the record can be carried off in bulk.
  // A skeleton that shows a greyed "CSV" and "Email a copy" for the length of the fetch,
  // and then removes them, does that same advertising and then looks broken doing it.
  const signedIn = !!context && !!context.signedInEmail;

  const buttons: HTMLElement[] = [
    el("button", { type: "button", class: "spr-btn", text: "Print", disabled: true, tabindex: "-1" })
  ];

  if (signedIn) {
    buttons.push(
      el("button", { type: "button", class: "spr-btn", text: "CSV", disabled: true, tabindex: "-1" }),
      el("button", {
        type: "button",
        class: "spr-btn spr-btn-primary",
        text: "Email a copy (CSV)",
        disabled: true,
        tabindex: "-1"
      }));
  }

  return el("div", { class: "spr-viewbar", "aria-hidden": "true" }, [
    el("span", { class: "spr-stamp", text: "Data read …" }),
    el("span", { class: "spr-spacer" }),
    ...buttons
  ]);
}

export function viewBar(options: RenderOptions, exportLive: HTMLElement): HTMLElement {
  const { report, context, requestExport } = options;

  const bar = el("div", { class: "spr-viewbar" }, [
    el("span", {
      class: "spr-stamp",
      "data-testid": "speaker-report-data-as-of",
      "data-read-at": report.readAtUtc,
      "data-time-zone": resolveZone(report.meeting.timeZoneId || context.timeZoneId),
      text: `Data read ${stampInZone(report.readAtUtc, report.meeting.timeZoneId || context.timeZoneId)}`
    }),
    el("span", { class: "spr-spacer" })
  ]);

  // Print is available to EVERY reader, signed in or not.
  //
  // It is the hand-off path -- this report gets folded into a Board packet and posted --
  // and it carries away nothing the reader is not already looking at, so none of the
  // reasoning below about bulk export applies to it. The stylesheet does the work; this
  // button exists so the affordance is visible on a page whose print rules are deliberate.
  const printButton = el("button", {
    type: "button",
    class: "spr-btn",
    "data-testid": "speaker-report-print",
    text: "Print",
    "aria-label": "Print this report, or save it as a PDF"
  });
  printButton.addEventListener("click", () => window.print());
  bar.appendChild(printButton);

  // A reader who is not signed in gets NO export control at all, not a disabled one.
  //
  // The distinction is deliberate and the two cases are genuinely different. To a signed-in
  // user, a disabled button with a reason is useful: it says "this exists, your account
  // cannot do it, go ask someone", which is a permission they can act on. To an anonymous
  // reader following a shared link, the same button is an invitation to nothing -- there is
  // no account to upgrade and no administrator to ask -- and it advertises that the record
  // can be carried off in bulk, on the one surface where that must not be possible.
  //
  // signedInEmail is server-rendered and empty exactly when the reader is anonymous.
  if (!context.signedInEmail) {
    return bar;
  }

  // The in-browser download. Built from the payload this reader already holds, so it can
  // widen access to nothing -- and it writes no contact detail even when the reader may see
  // it. Gated on being signed in for the same reason the emailed export is: on a shared
  // link there is no account behind the reader, and a one-click bulk copy of a speaker list
  // is not a control that belongs on that surface.
  //
  // A DELIBERATE FRICTION, NOT A CONTROL. Everything this button writes is already in the
  // anonymous reader's browser, so anyone who wants the file can rebuild it from the JSON.
  // Read the gate as a product decision about what the surface offers; do not rely on it,
  // and do not put anything behind it that the payload does not already carry.
  const csvButton = el("button", {
    type: "button",
    class: "spr-btn",
    "data-testid": "speaker-report-csv",
    text: "CSV",
    "aria-label": "Download every table on this report as a CSV file"
  });
  csvButton.addEventListener("click", () => {
    // CityzenWeb and ApiCore ship on separate pipelines, so a bundle newer than the API it
    // is talking to is a normal state during a rollout, and buildReportCsv walks half a
    // dozen collections. Without this the button is simply dead: no file, no announcement,
    // and nothing for the reader or a support ticket to go on.
    try {
      downloadCsv(csvFileName(report), buildReportCsv(report, options.root));
      announce(exportLive, "CSV downloaded.");
    } catch (error) {
      console.error("Speaker participation CSV could not be built.", error);
      announce(exportLive, "The CSV could not be built. Reload the report and try again.");
    }
  });
  bar.appendChild(csvButton);

  const exportButton = el("button", {
    type: "button",
    class: "spr-btn spr-btn-primary",
    "data-testid": "speaker-report-export",
    // "Email a copy", not "Export": the two controls beside it also export, and what
    // distinguishes this one is that the file arrives in a mailbox rather than in the
    // downloads folder. The format is named because it is the same format the CSV button
    // produces -- there is no spreadsheet library in this bundle and the server writes CSV,
    // so calling this one Excel would be a label that no file matches.
    text: "Email a copy (CSV)",
    disabled: requestExport === null,
    // Disabled-with-a-reason rather than hidden, for SIGNED-IN readers. A control that
    // vanishes reads as a missing feature; one that is disabled and says why reads as a
    // permission.
    title: requestExport === null
      ? "Your account cannot export this report. Ask an administrator."
      : "Email yourself a CSV, one row per sign-up"
  });

  if (requestExport !== null) {
    exportButton.addEventListener("click", () => {
      openExportDialog({
        host: options.root,
        trigger: exportButton,
        defaultEmail: context.signedInEmail,
        submit: (recipient) =>
          requestExport(recipient).then(() => {
            announce(exportLive, `Export queued. A link will be emailed to ${recipient}.`);
          })
      });
    });
  }

  bar.appendChild(exportButton);
  return bar;
}

// -- document header --------------------------------------------------------

/**
 * The document's header.
 *
 * Exported because the LOADING state renders it too. AC-12 requires a skeleton "in the
 * document's shape" whose arrival of content does not shift the header, and a header that
 * only exists once the payload lands cannot satisfy that -- everything below it moves when
 * it appears. It needs nothing from the report itself: the customer name is server-rendered
 * into the page context, so it can be placed immediately and never move.
 *
 * Both states MUST build it from here. Two copies of this markup would drift, and the
 * drift would show up as a layout shift that only appears on a slow connection.
 *
 * It titles the RECONCILIATION block. It previously titled a "What happened" block of stat
 * tiles that appeared nowhere in the approved prototype; that block was removed and this
 * insight now leads with the reconciliation table, so the id here matches the section
 * aria-labelledby below.
 */
export function blockHead(context: ReportContext): HTMLElement {
  return el("div", { class: "spr-block-head", "data-testid": "speaker-report-header" }, [
    el("h2", { id: "spr-recon-h", text: "Does it add up" }),
    el("p", { class: "spr-sub", text: context.customerName })
  ]);
}

/**
 * Two counts on one line, each carrying its own machine value.
 *
 * `statKey` is deliberately the camelCase key an assertion looks up, while the visible
 * label is the reader's wording -- "sign-ups", not "signUps".
 */
function countPair(
  firstValue: number,
  firstLabel: string,
  secondValue: number,
  secondLabel: string
): HTMLElement {
  const part = (value: number, label: string, statKey: string): HTMLElement =>
    el("span", { class: "spr-countpair", "data-stat": statKey, "data-value": value }, [
      el("b", { class: "spr-num", text: integer(value) }),
      el("span", { "data-stat-label": "", text: ` ${label}` })
    ]);

  return el("p", { class: "spr-count", "data-testid": "speaker-report-counts" }, [
    part(firstValue, firstLabel, "people"),
    el("span", { class: "spr-countsep", "aria-hidden": "true", text: " · " }),
    part(secondValue, secondLabel, "signUps")
  ]);
}

/**
 * `data-value` carries the MACHINE value and the visible text carries the formatted
 * one, so an assertion never has to parse a thousands separator or a translated
 * duration back out of the page.
 */
function statTile(key: string, label: string, value: string | number, note: string): HTMLElement {
  return el("div", {
    class: "spr-stat",
    "data-stat": key,
    "data-value": value
  }, [
    el("span", { class: "spr-stat-v", text: typeof value === "number" ? integer(value) : value }),
    el("span", { class: "spr-stat-k", "data-stat-label": "", text: label }),
    el("span", { class: "spr-stat-s", text: note })
  ]);
}

// -- reconciliation ---------------------------------------------------------

export function reconciliation(report: SpeakerParticipationReport): HTMLElement {
  const rows = report.reconciliation.map((row) =>
    el("div", {
      class: "spr-row",
      role: "row",
      "data-outcome-row": "",
      "data-outcome-label": outcomeLabel(row.outcome),
      "data-outcome": outcomeKey(row.outcome),
      "data-total": row.total
    }, [
      el("div", { class: "spr-cell", role: "rowheader", text: outcomeLabel(row.outcome) }),
      el("div", { class: "spr-cell spr-num", role: "gridcell", text: integer(row.inPerson) }),
      el("div", { class: "spr-cell spr-num", role: "gridcell", text: integer(row.phone) }),
      el("div", { class: "spr-cell spr-num spr-total", role: "gridcell", text: integer(row.total) })
    ]));

  const block = el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-reconciliation",
    "aria-labelledby": "spr-recon-h"
  }, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-recon-h", text: "Does it add up" }),
      el("p", {
        class: "spr-sub",
        text: "Spoke plus No show equals Signed up. Three outcomes, no fourth."
      })
    ]),
    // People and sign-ups stated together, and stated HERE, because the table below
    // counts sign-ups: a reader who takes its totals for a headcount is off by however
    // many people signed up for more than one item. That is the Orange County 11-vs-23
    // defect, and it is the whole reason this line exists.
    //
    // data-stat / data-value / data-stat-label are the machine hooks. They are the same
    // ones the deleted "What happened" tiles carried, so the assertion that these two
    // numbers differ still reads them without caring where they are drawn.
    countPair(report.totals.people, "people", report.totals.signUps, "sign-ups"),
    el("div", { class: "spr-grid spr-grid-recon", role: "grid" }, [
      el("div", { class: "spr-row spr-row-head", role: "row" }, [
        el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Outcome" }),
        el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "In person" }),
        el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "By phone" }),
        el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "Total" })
      ]),
      ...rows
    ])
  ]);

  // Both notices moved here when the "What happened" block was removed. This is now the
  // right home for them anyway: each one explains why the arithmetic in the table above
  // may not match what the reader expects, so they belong beside that arithmetic.
  //
  // The orphaned notice keeps its data-testid unchanged -- it is the only proof of AC-28,
  // and renaming it would break that test for a reason unrelated to what it asserts.
  if (report.totals.orphanedSignUps > 0) {
    block.appendChild(el("p", {
      class: "spr-notice",
      "data-testid": "speaker-report-orphaned-notice",
      text:
        `${report.totals.orphanedSignUps} sign-ups point at agenda items that no longer exist. ` +
        "They are not in any roster below and not in any total."
    }));
  }

  if (!report.availability.contactDetail) {
    block.appendChild(el("p", {
      class: "spr-notice spr-notice-quiet",
      text: "Contact details are withheld from your account, so speaker profiles show names only."
    }));
  }

  return block;
}

// -- agenda -----------------------------------------------------------------

/** What one agenda item's sign-ups add up to. Everything here is derived on the client
 *  from signUps[]; none of it needs a server round trip. */
interface ItemTally {
  inPersonSignedUp: number;
  inPersonSpoke: number;
  phoneSignedUp: number;
  phoneSpoke: number;
  /** Sum of the turns that WERE timed. */
  timedSeconds: number;
  timedCount: number;
  /** Sign-ups that spoke but were never timed. Stated, so the total is not read as
   *  the whole truth. */
  untimedSpokeCount: number;
  forCount: number;
  againstCount: number;
  neutralCount: number;
}

function tally(item: AgendaItem): ItemTally {
  const t: ItemTally = {
    inPersonSignedUp: 0, inPersonSpoke: 0,
    phoneSignedUp: 0, phoneSpoke: 0,
    timedSeconds: 0, timedCount: 0, untimedSpokeCount: 0,
    forCount: 0, againstCount: 0, neutralCount: 0
  };

  for (const signUp of item.signUps) {
    const spoke = signUp.outcome === "Spoke";

    if (signUp.channel === "Phone") {
      t.phoneSignedUp++;
      if (spoke) t.phoneSpoke++;
    } else {
      t.inPersonSignedUp++;
      if (spoke) t.inPersonSpoke++;
    }

    if (signUp.speakingSeconds !== null) {
      t.timedSeconds += signUp.speakingSeconds;
      t.timedCount++;
    } else if (spoke) {
      t.untimedSpokeCount++;
    }

    // Only turns that HAPPENED carry a position into the bar. A no-show's declared
    // position is real and is counted in the Breakdowns panel, but showing it here
    // would say the item was more contested than the meeting heard.
    if (spoke) {
      if (signUp.position === "For") t.forCount++;
      else if (signUp.position === "Against") t.againstCount++;
      else t.neutralCount++;
    }
  }

  return t;
}

/**
 * A channel's completion: how many of the people who signed up were actually heard.
 *
 * Renders NOTHING when nobody signed up on that channel. A "0%" there would say the
 * channel failed; the truth is it was not used, and those are different findings. Same
 * rule breakdowns() already applies by returning null on an empty report.
 */
function channelCell(
  signedUp: number,
  spoke: number,
  channel: "in-person" | "phone",
  coverage: string
): HTMLElement {
  // data-column-label carries the READER's wording; data-column-key stays the machine
  // key a test addresses. The stacked layout below 720px prints the label, so it must
  // not be the kebab-case key.
  const cell = el("div", {
    class: "spr-cell spr-num",
    role: "gridcell",
    "data-column-key": channel,
    "data-column-label": channel === "phone" ? "By phone" : "In person",
    // What the column knows, on the column. "By phone" is the REGISTRATION's answer, not
    // the bridge's observation, and a reader who takes it for the latter has read a
    // different fact off the same number.
    title: coverage
  });

  if (signedUp === 0) {
    cell.appendChild(el("span", { class: "spr-untimed", "aria-hidden": "true", text: "—" }));
    // The dash is decoration; the accessible name has to say what it means.
    cell.appendChild(el("span", { class: "spr-sr", text: `No ${channel} sign-ups` }));
    return cell;
  }

  const percent = Math.round((spoke / signedUp) * 100);

  cell.setAttribute("data-signed-up", String(signedUp));
  cell.setAttribute("data-spoke", String(spoke));
  cell.setAttribute("data-percent", String(percent));

  cell.appendChild(el("span", { class: "spr-ratio" }, [
    el("b", { text: integer(spoke) }),
    el("span", { class: "spr-ratio-of", text: ` / ${integer(signedUp)}` })
  ]));
  // The bar is a named image rather than decoration: its mouseover is the only place the
  // three figures sit in one sentence, and a tooltip a keyboard user cannot reach is not an
  // equivalent, so the same sentence is its accessible name.
  const tip = barTip(channel === "phone" ? "By phone" : "In person", spoke, signedUp);
  cell.appendChild(el("span", {
    class: "spr-bar spr-bar-group",
    role: "img",
    "aria-label": tip,
    title: tip,
    style: `--spr-fill:${percent}%`
  }));
  cell.appendChild(el("span", { class: "spr-bar-note", text: `${percent}% spoke` }));

  return cell;
}

/** Total speaking time on the item, stating how much of it was actually measured. */
function timeCell(t: ItemTally): HTMLElement {
  const cell = el("div", {
    class: "spr-cell spr-num",
    role: "gridcell",
    "data-column-key": "time",
    "data-column-label": "Time"
  });

  if (t.timedCount === 0) {
    for (const span of untimedSpans()) cell.appendChild(span);
    return cell;
  }

  cell.setAttribute("data-timed-seconds", String(t.timedSeconds));
  cell.setAttribute("data-timed-count", String(t.timedCount));
  cell.appendChild(el("span", { class: "spr-len", text: duration(t.timedSeconds) }));

  // An item where the clerk timed three of five turns must not present its total as
  // the item's whole speaking time. Saying so is the difference between a figure and
  // a misleading one.
  if (t.untimedSpokeCount > 0) {
    cell.appendChild(el("span", {
      class: "spr-bar-note",
      text: `over ${t.timedCount} of ${t.timedCount + t.untimedSpokeCount} timed`
    }));
  }

  return cell;
}

/** For / against / neutral across the turns that were actually heard. */
function positionCell(t: ItemTally): HTMLElement {
  const cell = el("div", {
    class: "spr-cell",
    role: "gridcell",
    "data-column-key": "position",
    "data-column-label": "Position"
  });

  const total = t.forCount + t.againstCount + t.neutralCount;
  if (total === 0) {
    cell.appendChild(el("span", { class: "spr-untimed", "aria-hidden": "true", text: "—" }));
    cell.appendChild(el("span", { class: "spr-sr", text: "Nobody was heard on this item" }));
    return cell;
  }

  cell.setAttribute("data-for", String(t.forCount));
  cell.setAttribute("data-against", String(t.againstCount));
  cell.setAttribute("data-neutral", String(t.neutralCount));

  cell.appendChild(positionStack([
    ["for", "In favor", t.forCount],
    ["against", "Opposed", t.againstCount],
    ["neutral", "Neutral", t.neutralCount]
  ]));
  // Icons and counts, not letters: "1F · 0O · 2N" asked the reader to learn a key.
  cell.appendChild(positionTally(t.forCount, t.againstCount, t.neutralCount));

  return cell;
}

/**
 * A stacked proportion bar whose every segment names itself on hover, and whose group names
 * all of them for a keyboard or screen reader user, who cannot hover.
 *
 * Zero segments are kept, with their tooltip, so the three tracks are always present.
 */
function positionStack(segments: Array<[string, string, number]>): HTMLElement {
  const total = segments.reduce((sum, [, , n]) => sum + n, 0);
  const tips = segments.map(([, label, n]) => barTip(label, n, total));

  return el("span", {
    class: "spr-bar-stack spr-bar-group",
    role: "img",
    "aria-label": tips.join("; ")
  }, segments.map(([key, , n], index) =>
    el("span", {
      class: "spr-bar-" + key,
      title: tips[index],
      style: `--spr-fill:${total === 0 ? 0 : Math.round((n / total) * 100)}%`
    })));
}

/** #rgb, #rrggbb or #rrggbbaa, anchored. A partial match would accept
 *  "#fff; background: url(...)". */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * An agenda item's customer-defined status.
 *
 * The colour is validated AGAIN here even though the server already sanitised it. That is
 * not belt-and-braces theatre: the value originates with a customer admin, is stored in an
 * unvalidated nvarchar(max), and a client that simply trusts the server is one backend
 * regression away from writing attacker-controlled text into a style attribute.
 *
 * It is also applied as a DOT and a left border, never as text or as a text background.
 * An arbitrary customer hex cannot be guaranteed to clear 4.5:1 against white -- #ffff00
 * is 1.07:1 -- but as a non-text indicator it only has to clear 3:1, and the status NAME
 * beside it carries the meaning regardless. Colour is never the only channel.
 */
function statusBadge(name: string, color: string | null): HTMLElement {
  const badge = el("span", {
    class: "spr-status",
    "data-testid": "speaker-report-status-badge",
    "data-status-name": name,
    text: name
  });

  if (color && HEX_COLOR.test(color)) {
    // setProperty, not a style string: the value can never escape into another
    // declaration, whatever it contains.
    badge.style.setProperty("--spr-status", color);
    badge.setAttribute("data-status-color", color);
  } else {
    badge.setAttribute("data-status-color", "");
  }

  return badge;
}

/** Sortable agenda columns. A null key is agenda order, which is the third state. */
type AgendaSortKey = "item" | "title" | "in-person" | "phone" | "time" | "position";

interface AgendaSort {
  key: AgendaSortKey | null;
  direction: "ascending" | "descending";
}

/** One flattened item plus the section it came from, so a filtered view can still say
 *  where each item sits without re-walking the tree. */
interface FlatItem {
  item: AgendaItem;
  sectionTitle: string;
  tally: ItemTally;
}

/**
 * Compares two sort values of the same kind.
 *
 * Titles compare with localeCompare rather than with `<`: the agendas this report is
 * built for carry accented and non-English item titles, and codepoint order puts every
 * one of those after "Z".
 */
function compareSortValues(a: number | string, b: number | string): number {
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b));
  }
  return a - b;
}

function sortValue(row: FlatItem, key: AgendaSortKey): number | string {
  const t = row.tally;
  switch (key) {
    // Alphabetical by the printed title, case-folded so "Zoning" and "zoning" sit
    // together rather than in two separate runs.
    case "title":
      return row.item.title.trim().toLocaleLowerCase();
    // Completion RATE, not the raw count: "which items lost their phone callers" is the
    // question, and 2/2 is a better showing than 9/20. A channel nobody used sorts below
    // every real rate rather than tying with a total failure.
    case "in-person":
      return t.inPersonSignedUp === 0 ? -1 : t.inPersonSpoke / t.inPersonSignedUp;
    case "phone":
      return t.phoneSignedUp === 0 ? -1 : t.phoneSpoke / t.phoneSignedUp;
    case "time":
      return t.timedSeconds;
    case "position":
      return t.forCount + t.againstCount + t.neutralCount;
    default:
      return row.item.displayOrder;
  }
}

function matchesQuery(row: FlatItem, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();

  if (row.item.itemNumber.toLowerCase().includes(q)) return true;
  if (row.item.title.toLowerCase().includes(q)) return true;
  if (row.sectionTitle.toLowerCase().includes(q)) return true;

  // Matching a SPEAKER keeps their whole item visible. A clerk searching a name wants
  // the item that person spoke on, not a roster row torn out of its context.
  return row.item.signUps.some((signUp) =>
    signUp.speakerName.toLowerCase().includes(q) ||
    signUp.organization.toLowerCase().includes(q));
}

export function agenda(report: SpeakerParticipationReport, root: HTMLElement): HTMLElement {
  const byUserId: Record<string, SpeakerRecord> = {};
  for (const person of report.directory) byUserId[person.userId] = person;

  const block = el("section", { class: "spr-block", "aria-labelledby": "spr-agenda-h" }, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-agenda-h", text: "Agenda and who signed up" }),
      el("p", { class: "spr-sub", text: "In agenda order. Open an item to see its roster." })
    ])
  ]);

  if (report.sections.length === 0) {
    block.appendChild(el("p", {
      class: "spr-empty",
      text: report.meeting.agendaPublished
        ? "This meeting has a published agenda with no sections or items."
        : "This meeting has no published agenda, so there is nothing to reconcile speakers against."
    }));
    return block;
  }

  if (report.totals.signUps === 0) {
    // Distinct from a filtered-to-nothing state on purpose. "Nobody signed up to speak"
    // is a finding about the meeting; "your search matched nothing" is a finding about
    // the search, and a clerk reading the wrong one draws the wrong conclusion.
    block.appendChild(el("p", {
      class: "spr-notice",
      "data-testid": "speaker-report-no-speakers",
      text:
        "No speakers registered for this meeting. The agenda is shown below so the record " +
        "is complete."
    }));
  }

  const flat: FlatItem[] = [];
  for (const section of report.sections) {
    for (const item of section.items) {
      flat.push({ item, sectionTitle: section.title || "", tally: tally(item) });
    }
  }

  const sort: AgendaSort = { key: null, direction: "ascending" };
  let query = "";

  const results = el("div", { "data-testid": "speaker-report-agenda-results", "data-filter": "" });
  const count = el("p", {
    class: "spr-count",
    "data-testid": "speaker-report-agenda-count",
    role: "status",
    "aria-live": "polite"
  });

  const search = el("input", {
    type: "search",
    id: "spr-agenda-search",
    class: "spr-input",
    "data-testid": "speaker-report-agenda-search",
    placeholder: "Search item number, title, section, speaker or organization",
    "aria-label": "Search the agenda by item number, title, section, speaker name or organization"
  }) as HTMLInputElement;

  const clear = el("button", {
    type: "button",
    class: "spr-btn",
    "data-testid": "speaker-report-agenda-clear",
    text: "Clear",
    hidden: true
  });

  // No item-to-item pager. The owner removed the Previous / Next item buttons on 2026-09-14
  // ("scroll works fine"); the item count above the grid is what the prototype shows.
  block.appendChild(el("div", { class: "spr-search" }, [search, clear]));
  block.appendChild(count);
  block.appendChild(legend(report));
  block.appendChild(results);

  function applySort(key: AgendaSortKey): void {
    // Three states, ending back at AGENDA ORDER. A sort a reader cannot undo strands
    // them in an order the printed agenda does not use, which for a public record is
    // worse than not sorting at all.
    if (sort.key !== key) {
      sort.key = key;
      sort.direction = "ascending";
    } else if (sort.direction === "ascending") {
      sort.direction = "descending";
    } else {
      sort.key = null;
      sort.direction = "ascending";
    }

    draw();

    // The header just activated has been replaced by the redraw. Put focus on its
    // replacement, or the reader is silently dropped to the top of the document.
    const restored = results.querySelector<HTMLElement>('[data-agenda-sort="' + key + '"]');
    restored?.focus();
  }

  function draw(): void {
    const visible = flat.filter((row) => matchesQuery(row, query));

    if (sort.key !== null) {
      const key = sort.key;
      const factor = sort.direction === "ascending" ? 1 : -1;
      // Agenda order is the tiebreak, so equal rows keep the order of the printed
      // agenda instead of whatever the sort happened to leave behind.
      visible.sort((a, b) => {
        const compared = compareSortValues(sortValue(a, key), sortValue(b, key));
        if (compared === 0) return a.item.displayOrder - b.item.displayOrder;
        return compared * factor;
      });
    }

    if (visible.length === 0) {
      replaceChildrenWith(results, [el("p", {
        class: "spr-empty",
        "data-testid": "speaker-report-agenda-no-match",
        text: 'No agenda item matches "' + query + '".'
      })]);
    } else {
      const grid = el("div", {
        class: "spr-grid spr-grid-agenda",
        role: "grid",
        "aria-label": "Agenda items",
        "data-sort-state": sort.key === null ? "agenda" : sort.direction,
        "data-sort-key": sort.key ?? "agenda"
      }, [agendaHeaderRow(sort, applySort)]);

      const showSection = sort.key !== null || query !== "";
      for (const row of visible) {
        grid.appendChild(agendaRow(row, report, root, byUserId, showSection));
      }

      // Suppressed when nobody signed up, matching breakdowns() returning null: a row of
      // zeroes there contradicts the "nothing to reconcile" notice above it.
      if (report.totals.signUps > 0) {
        grid.appendChild(totalsRow(visible, flat.length, channelCoverageNote(report.timeProfile.timeline)));
      }

      replaceChildrenWith(results, [grid]);
    }

    count.textContent = query
      ? visible.length + " of " + flat.length + ' items match "' + query + '"'
      : flat.length + " items";
    clear.hidden = query === "";
    results.setAttribute("data-filter", query);
  }

  search.addEventListener("input", () => {
    query = search.value.trim();
    draw();
  });

  clear.addEventListener("click", () => {
    query = "";
    search.value = "";
    draw();
    search.focus();
  });

  draw();
  return block;
}

/**
 * The colour key.
 *
 * Exported and called by EVERY insight that uses these colours, not once by the agenda.
 * The five insights render in independent containers, so a clerk who embedded only the
 * directory would otherwise be shown colour-coded chips with no key anywhere on screen.
 */
export function legend(report: SpeakerParticipationReport): HTMLElement {
  const entries: Array<[string, string]> = [
    ["in-person", "In person"],
    ["phone", "By phone"]
  ];

  if (report.availability.positions) {
    entries.push(["for", "In favor"], ["against", "Opposed"], ["neutral", "Neutral or unset"]);
  }

  return el("ul", { class: "spr-legend", "data-testid": "speaker-report-legend" },
    entries.map(([key, label]) =>
      el("li", { class: "spr-legend-item", "data-legend-key": key }, [
        el("span", { class: "spr-legend-swatch spr-swatch-" + key, "aria-hidden": "true" }),
        el("span", { text: label })
      ])));
}

function agendaHeaderRow(sort: AgendaSort, onSort: (key: AgendaSortKey) => void): HTMLElement {
  // Six columns, matching the item row below track for track. ITEM is the disclosure and
  // the number; TITLE is its own column and is sortable in its own right, because
  // "find the item called X" is a real question and the number is no help with it.
  const columns: Array<[AgendaSortKey, string, boolean]> = [
    ["item", "Item", false],
    ["title", "Title", false],
    ["in-person", "In person", true],
    ["phone", "By phone", true],
    ["time", "Time", true],
    ["position", "Position", true]
  ];

  const row = el("div", { class: "spr-row spr-row-head", role: "row" });

  for (const [key, label, numeric] of columns) {
    const cell = el("div", {
      class: "spr-cell spr-cell-head" + (numeric ? " spr-num" : ""),
      role: "columnheader"
    });

    const active = sort.key === key;

    // aria-sort is only valid on a real table columnheader, and this is a grid, so the
    // direction lives in the accessible NAME -- the same contract roster.ts uses. The
    // third state is named too: a reader must be told the next activation restores
    // agenda order rather than discovering it.
    const name = active
      ? sort.direction === "ascending"
        ? label + ", sorted ascending. Activate to sort descending."
        : label + ", sorted descending. Activate to return to agenda order."
      : label + ", not sorted. Activate to sort ascending.";

    const button = el("button", {
      type: "button",
      class: "spr-sort" + (active ? " is-active" : ""),
      "data-agenda-sort": key,
      "data-sort-direction": active ? sort.direction : "none",
      "aria-label": name
    }, [
      el("span", { text: label }),
      // EVERY sortable column carries a mark, including the unsorted ones. An indicator
      // that appears only once a column is already sorted tells a reader nothing about
      // which columns they may sort -- it is a state readout where an affordance is
      // wanted. The neutral glyph is decoration; the direction is in the name above.
      el("span", {
        class: "spr-sort-mark",
        "aria-hidden": "true",
        text: active ? (sort.direction === "ascending" ? "▲" : "▼") : "⇅"
      })
    ]);

    button.addEventListener("click", () => onSort(key));
    cell.appendChild(button);
    row.appendChild(cell);
  }

  // No seventh header. The disclosure used to occupy a column of its own on the right and
  // needed an off-screen name; it now sits under ITEM, which is a named column already.
  return row;
}

function agendaRow(
  row: FlatItem,
  report: SpeakerParticipationReport,
  root: HTMLElement,
  byUserId: Record<string, SpeakerRecord>,
  showSection: boolean
): HTMLElement {
  const item = row.item;
  const t = row.tally;
  const coverage = channelCoverageNote(report.timeProfile.timeline);

  const roster = renderRoster({
    item,
    availability: report.availability,
    turns: turnIndexFor(report),
    renderCap: ROSTER_RENDER_CAP,
    onSpeakerActivated: (userId, trigger) => {
      const person = byUserId[userId];
      if (person) {
        openProfile({
          person,
          availability: report.availability,
          turns: turnIndexFor(report),
          trigger,
          host: root
        });
      }
    }
  });

  // The status badge is added BEFORE the title, not after.
  //
  // .spr-item-title claims a whole flex line, so whatever precedes it sits on the line
  // above. Appending the badge last put it under the title; the approved design puts the
  // item's state above the words it qualifies, which is also the order a reader scanning
  // a column of statuses needs.
  //
  // The item NUMBER is no longer here at all -- it moved into the disclosure control in
  // the ITEM column, so this cell is the title column and nothing else.
  const ident = el("div", {
    class: "spr-cell spr-item-ident",
    role: "rowheader",
    "data-column-label": "Title"
  });

  if (item.statusName) {
    ident.appendChild(statusBadge(item.statusName, item.statusColor));
  }

  ident.appendChild(el("span", { class: "spr-item-title", text: item.title }));

  // Once the list is filtered or re-sorted the section headings no longer bracket their
  // rows, so each row carries its own section or the reader loses track of where in the
  // agenda they are.
  if (showSection && row.sectionTitle) {
    ident.appendChild(el("span", { class: "spr-item-section", text: row.sectionTitle }));
  }

  // The allotment is deliberately NOT on the item head any more. "3:00 each" told a
  // reader the rule; the useful fact is which speakers ran up against it, so the figure
  // now appears per speaker in the roster as a percentage used.
  // ITEM first, then TITLE. The disclosure control carries the item number and leads the
  // row, so the number reads as its own column rather than as a prefix glued to the
  // title, and the title track gets every pixel the old 110px disclosure column held.
  const head = el("div", { class: "spr-item-head", role: "row" }, [
    el("div", {
      class: "spr-cell spr-item-disclose",
      role: "gridcell",
      "data-column-label": "Item"
    }, [roster.toggle]),
    ident,
    channelCell(t.inPersonSignedUp, t.inPersonSpoke, "in-person", coverage),
    channelCell(t.phoneSignedUp, t.phoneSpoke, "phone", coverage),
    timeCell(t),
    positionCell(t)
  ]);

  return el("div", {
    class: "spr-item",
    "data-testid": "speaker-report-agenda-item",
    "data-agenda-item-id": item.id,
    "data-signups": String(item.signUps.length)
  }, [
    head,
    // The panel sits in its own row holding ONE gridcell, with the roster's own
    // role="grid" nested inside it. A bare div child of a role="grid" reproduces exactly
    // the aria-required-parent failure roster.ts warns about at its head.
    el("div", { class: "spr-item-panel-row", role: "row" }, [
      el("div", { class: "spr-cell spr-item-panel", role: "gridcell" }, [roster.panel])
    ])
  ]);
}

/**
 * The bottom line: items, sign-ups and PEOPLE as three different numbers.
 *
 * People and sign-ups differ by however many speakers signed up for more than one item,
 * and a reader who takes one for the other is reading the Orange County 11-vs-23 defect.
 * Relabels when a filter is applied, so a partial total is never mistaken for the whole.
 */
function totalsRow(visible: FlatItem[], totalItems: number, coverage: string): HTMLElement {
  const t: ItemTally = {
    inPersonSignedUp: 0, inPersonSpoke: 0, phoneSignedUp: 0, phoneSpoke: 0,
    timedSeconds: 0, timedCount: 0, untimedSpokeCount: 0,
    forCount: 0, againstCount: 0, neutralCount: 0
  };
  const people = new Set<string>();
  let signUps = 0;

  for (const row of visible) {
    t.inPersonSignedUp += row.tally.inPersonSignedUp;
    t.inPersonSpoke += row.tally.inPersonSpoke;
    t.phoneSignedUp += row.tally.phoneSignedUp;
    t.phoneSpoke += row.tally.phoneSpoke;
    t.timedSeconds += row.tally.timedSeconds;
    t.timedCount += row.tally.timedCount;
    t.untimedSpokeCount += row.tally.untimedSpokeCount;
    t.forCount += row.tally.forCount;
    t.againstCount += row.tally.againstCount;
    t.neutralCount += row.tally.neutralCount;

    for (const signUp of row.item.signUps) {
      signUps++;
      people.add(signUp.userId);
    }
  }

  const filtered = visible.length !== totalItems;
  const scope = filtered
    ? integer(visible.length) + " of " + integer(totalItems) + " items"
    : integer(visible.length) + " items";

  return el("div", {
    class: "spr-row spr-row-total",
    role: "row",
    "data-testid": "speaker-report-agenda-totals",
    "data-items": String(visible.length),
    "data-signups": String(signUps),
    "data-people": String(people.size)
  }, [
    // The ITEM column holds a sigma rather than a number and rather than nothing: the
    // track exists on every other row, so leaving it blank reads as a missing value
    // instead of as "this row is the sum of the column above".
    el("div", { class: "spr-cell spr-item-disclose", role: "gridcell" }, [
      el("span", { class: "spr-item-sum", "aria-hidden": "true", text: "Σ" })
    ]),
    el("div", { class: "spr-cell spr-item-ident", role: "rowheader" }, [
      el("span", { class: "spr-total", text: filtered ? "Totals — filtered" : "Totals" }),
      el("span", {
        class: "spr-bar-note",
        text: scope + " · " + integer(signUps) + " sign-ups · " +
          integer(people.size) + " people"
      })
    ]),
    channelCell(t.inPersonSignedUp, t.inPersonSpoke, "in-person", coverage),
    channelCell(t.phoneSignedUp, t.phoneSpoke, "phone", coverage),
    timeCell(t),
    positionCell(t)
  ]);
}

// -- directory --------------------------------------------------------------

export function directory(
  report: SpeakerParticipationReport,
  root: HTMLElement,
  context: ReportContext
): HTMLElement {
  const pageSize = context.directoryPageSize;
  // Computed once for the whole directory rather than per row: this walks every roster,
  // and doing it inside directoryRow would re-walk them on every page turn.
  const languages = languagesByUserId(report);
  let filter = "";
  let page = 0;

  const list = el("div", { class: "spr-grid spr-grid-directory", role: "grid" });
  const summary = el("p", {
    class: "spr-count",
    "data-testid": "speaker-report-directory-summary"
  });
  const pager = el("div", { class: "spr-pager" });

  const panel = el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-directory",
    "data-filter": "",
    "aria-labelledby": "spr-directory-h"
  });

  const search = el("input", {
    type: "search",
    id: "spr-directory-search",
    class: "spr-input",
    "aria-label": "Search speakers by name or organisation",
    placeholder: "Search speakers"
  });

  const matching = (): SpeakerRecord[] => {
    const needle = filter.trim().toLowerCase();
    if (needle.length === 0) return report.directory;

    return report.directory.filter((person) =>
      person.name.toLowerCase().indexOf(needle) >= 0 ||
      person.organization.toLowerCase().indexOf(needle) >= 0);
  };

  const draw = (): void => {
    const rows = matching();
    const pages = Math.max(1, Math.ceil(rows.length / pageSize));
    if (page >= pages) page = pages - 1;

    const slice = rows.slice(page * pageSize, page * pageSize + pageSize);

    // Headers are built from the same availability flags the rows are, so a column can
    // never appear in one and not the other.
    const headers: HTMLElement[] = [
      el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Speaker" })
    ];
    if (report.availability.organizations) {
      headers.push(el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Organization" }));
    }
    headers.push(
      el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Attended" }),
      el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "Items" }),
      el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "Spoke" }),
      el("div", { class: "spr-cell spr-cell-head spr-num", role: "columnheader", text: "Total time" }));
    if (report.availability.languages) {
      headers.push(el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Language" }));
    }
    if (report.availability.positions) {
      headers.push(el("div", { class: "spr-cell spr-cell-head", role: "columnheader", text: "Position" }));
    }

    replaceChildrenWith(list, [
      el("div", { class: "spr-row spr-row-head", role: "row" }, headers),
      ...slice.map((person) => directoryRow(person, report, root, languages[person.userId] ?? []))
    ]);

    if (rows.length === 0) {
      list.appendChild(el("div", {
        class: "spr-empty",
        text: "No speaker matches that search."
      }));
    }

    summary.textContent = filter.trim().length === 0
      ? `${integer(rows.length)} speakers`
      : `${integer(rows.length)} of ${integer(report.directory.length)} speakers match`;

    replaceChildrenWith(pager, pages > 1 ? [
      pageButton("‹ Previous", page === 0, () => { page -= 1; draw(); }),
      el("span", { class: "spr-page-of", text: `Page ${page + 1} of ${pages}` }),
      pageButton("Next ›", page >= pages - 1, () => { page += 1; draw(); })
    ] : []);
  };

  search.addEventListener("input", () => {
    filter = search.value;
    page = 0;
    // The filter goes on the panel so a test can wait on applied STATE rather than
    // on a timer. State-based waits only.
    panel.setAttribute("data-filter", filter);
    draw();
  });

  replaceChildrenWith(panel, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-directory-h", text: "Speaker directory" }),
      el("p", {
        class: "spr-sub",
        text: "One row per person. A person who signed up for three items appears once here and three times above."
      })
    ]),
    el("div", { class: "spr-search" }, [search]),
    summary,
    list,
    pager
  ]);

  draw();
  return panel;
}

/**
 * Languages per person, derived on the client.
 *
 * SpeakerRecord does not carry a language list -- language lives on the SIGN-UP, because
 * a person can be interpreted on one item and not another. Joining the rosters by user id
 * is the same reduction the server would do, without a contract change.
 */
function languagesByUserId(report: SpeakerParticipationReport): Record<string, string[]> {
  const found: Record<string, Set<string>> = {};

  for (const section of report.sections) {
    for (const item of section.items) {
      for (const signUp of item.signUps) {
        // Collected by DISPLAY name, so a person who is `en` on one item and `en-US` on
        // another is one "English" chip, not two chips that read the same.
        const name = languageName(signUp.languageCode);
        if (!name) continue;
        (found[signUp.userId] ??= new Set<string>()).add(name);
      }
    }
  }

  const out: Record<string, string[]> = {};
  for (const userId of Object.keys(found)) out[userId] = Array.from(found[userId]).sort();
  return out;
}

/** For / against / neutral across the items this person was actually heard on. */
function positionSplit(person: SpeakerRecord): { forCount: number; against: number; neutral: number } {
  let forCount = 0;
  let against = 0;
  let neutral = 0;

  for (const ref of person.spokeOn) {
    if (ref.outcome !== "Spoke") continue;
    if (ref.position === "For") forCount++;
    else if (ref.position === "Against") against++;
    else neutral++;
  }

  return { forCount, against, neutral };
}

function directoryRow(
  person: SpeakerRecord,
  report: SpeakerParticipationReport,
  root: HTMLElement,
  languages: string[]
): HTMLElement {
  const name = el("button", {
    type: "button",
    class: "spr-name",
    "data-testid": "speaker-report-directory-name",
    "data-speaker-name": person.name,
    "data-user-id": person.userId,
    text: person.name
  });

  name.addEventListener("click", () => {
    openProfile({
      person,
      availability: report.availability,
      turns: turnIndexFor(report),
      trigger: name,
      host: root
    });
  });

  // Organization is its own column now, so it is not repeated under the name.
  const speaker = el("div", { class: "spr-cell", role: "gridcell" }, [name]);

  const cells: HTMLElement[] = [speaker];

  if (report.availability.organizations) {
    cells.push(el("div", { class: "spr-cell", role: "gridcell", "data-column-key": "organization" }, [
      ...(person.organization
        ? [el("span", { text: person.organization })]
        : blankSpans("None given"))
    ]));
  }

  cells.push(el("div", { class: "spr-cell", role: "gridcell", "data-column-key": "channel" }, [
    el("span", {
      class: `spr-chip spr-chip-channel spr-channel-${channelKey(person.channel)}`,
      text: channelLabel(person.channel)
    })
  ]));

  cells.push(el("div", {
    class: "spr-cell spr-num", role: "gridcell", "data-column-key": "items",
    text: integer(person.signUpCount)
  }));
  cells.push(el("div", {
    class: "spr-cell spr-num", role: "gridcell", "data-column-key": "spoke",
    text: integer(person.spokeCount)
  }));
  cells.push(el("div", {
    class: "spr-cell spr-num", role: "gridcell", "data-column-key": "time"
  }, person.totalSpeakingSeconds === null || person.totalSpeakingSeconds === undefined
    ? untimedSpans()
    : [el("span", { class: "spr-len", text: duration(person.totalSpeakingSeconds) })]));

  if (report.availability.languages) {
    cells.push(el("div", { class: "spr-cell", role: "gridcell", "data-column-key": "language" },
      languages.length > 0
        ? languages.map((name) => el("span", { class: "spr-chip", text: name }))
        : blankSpans("None given")));
  }

  if (report.availability.positions) {
    const split = positionSplit(person);
    cells.push(el("div", {
      class: "spr-cell", role: "gridcell", "data-column-key": "position",
      "data-for": String(split.forCount),
      "data-against": String(split.against),
      "data-neutral": String(split.neutral)
    }, [positionTally(split.forCount, split.against, split.neutral)]));
  }

  return el("div", {
    class: "spr-row",
    role: "row",
    "data-user-id": person.userId,
    "data-channel": channelKey(person.channel)
  }, cells);
}

function pageButton(label: string, disabled: boolean, onClick: () => void): HTMLElement {
  const button = el("button", {
    type: "button",
    class: "spr-btn spr-btn-sm",
    disabled,
    text: label
  });
  if (!disabled) button.addEventListener("click", onClick);
  return button;
}

/**
 * Published dashboards for the board this meeting belongs to.
 *
 * Returns null when there are none, so the block is ABSENT rather than an empty panel --
 * most boards have no dashboard published, and a heading over nothing reads as a fault.
 *
 * Every link here is already public at its shortcode URL, which is why it is shown to any
 * reader who can see the report rather than to signed-in staff only.
 */
export function dashboards(report: SpeakerParticipationReport): HTMLElement | null {
  // CityzenWeb and ApiCore deploy on separate pipelines at separate cadences, so a
  // bundle newer than the API it is talking to is a normal state during a rollout,
  // not an edge case. `dashboards` is new in this release, so against an older API it
  // arrives undefined -- and an unguarded .length here took this entire insight down
  // on dev. Absent means none.
  const links = report.dashboards ?? [];
  if (links.length === 0) return null;

  return el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-dashboards",
    "aria-labelledby": "spr-dash-h"
  }, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-dash-h", text: "Meeting dashboards" }),
      el("p", {
        class: "spr-sub",
        text: "Published dashboards for this board. They open in the public view."
      })
    ]),
    el("ul", { class: "spr-dash-list" }, links.map((dash) =>
      el("li", { class: "spr-dash-item" }, [
        el("a", {
          class: "spr-dash-link",
          "data-testid": "speaker-report-dashboard-link",
          "data-shortcode": dash.shortcode,
          href: "/meeting-dashboard/" + encodeURIComponent(dash.shortcode),
          text: dash.name
        }),
        dash.description
          ? el("span", { class: "spr-dash-desc", text: dash.description })
          : null
      ].filter(Boolean) as HTMLElement[]))
    )
  ]);
}

// -- breakdowns and time ----------------------------------------------------

/**
 * Sign-ups per agenda SECTION, ranked. Derived on the client from the section tree; the
 * server ships four breakdowns and section is not one of them, but the tree already
 * carries everything the reduction needs.
 */
function bySectionPanel(report: SpeakerParticipationReport): HTMLElement {
  const slices: BreakdownSlice[] = report.sections
    .map((section) => ({
      key: String(section.id),
      label: section.title || "Unsectioned",
      count: section.items.reduce((sum, item) => sum + item.signUps.length, 0)
    }))
    .filter((slice) => slice.count > 0)
    .sort((a, b) => b.count - a.count);

  return breakdownPanel("By agenda section", slices, (slice) => slice.label);
}

/**
 * Positions that were registered and never heard.
 *
 * The Position panel counts every declared position, including the no-shows. Without
 * this line a reader compares it against the room and concludes the report is wrong;
 * with it, the gap is the finding -- these people took a position and did not get to
 * state it, and they remain in the record and in the export.
 */
function unheardNote(report: SpeakerParticipationReport): HTMLElement | null {
  if (!report.availability.positions) return null;

  let unheard = 0;
  let unheardFor = 0;
  let unheardAgainst = 0;

  for (const section of report.sections) {
    for (const item of section.items) {
      for (const signUp of item.signUps) {
        if (signUp.outcome === "Spoke" || signUp.position === "Unset") continue;
        unheard++;
        if (signUp.position === "For") unheardFor++;
        else if (signUp.position === "Against") unheardAgainst++;
      }
    }
  }

  if (unheard === 0) {
    return el("p", {
      class: "spr-notice spr-notice-quiet",
      "data-testid": "speaker-report-unheard",
      "data-unheard": "0",
      text: "Every registered position was heard."
    });
  }

  return el("p", {
    class: "spr-notice",
    "data-testid": "speaker-report-unheard",
    "data-unheard": String(unheard),
    "data-unheard-for": String(unheardFor),
    "data-unheard-against": String(unheardAgainst),
    text: unheard + " registered positions were never heard — " + unheardFor +
      " in favor, " + unheardAgainst + " opposed. They stay in the record and in the export."
  });
}

export function breakdowns(report: SpeakerParticipationReport): HTMLElement | null {
  // Absent, not empty. A meeting nobody signed up for has nothing to cross-tabulate, and
  // a panel of zeroes reads as a finding rather than as an absence.
  if (report.totals.signUps === 0) return null;

  const panels: HTMLElement[] = [];

  // A panel is OMITTED when its data is unavailable rather than drawn full of
  // zeroes. Zeroes read as a finding; absence reads as "not collected".
  if (report.breakdowns.position.length > 0) {
    panels.push(breakdownPanel("Position", report.breakdowns.position, positionLabelFor));
  }
  // The channel panel carries the coverage line the agenda cells carry as a tooltip. A
  // cross-tab is where a reader decides what the split MEANS, so the caveat has to be
  // readable rather than hoverable at the one place it changes the reading.
  panels.push(breakdownPanel(
    "Channel",
    report.breakdowns.channel,
    channelLabelFor,
    channelCoverageNote(report.timeProfile.timeline)));
  panels.push(breakdownPanel("Completion", report.breakdowns.completion, completionLabelFor));
  panels.push(bySectionPanel(report));
  if (report.breakdowns.segments.length > 0) {
    panels.push(breakdownPanel("Segments", report.breakdowns.segments, (slice) => slice.label));
  }

  if (panels.length === 0) return null;

  const block = el("section", {
    class: "spr-block",
    "data-testid": "speaker-report-breakdowns",
    "aria-labelledby": "spr-breakdowns-h"
  }, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-breakdowns-h", text: "Breakdowns" }),
      el("p", { class: "spr-sub", text: "Counted by sign-up, not by person." })
    ]),
    el("div", { class: "spr-panels" }, panels)
  ]);

  const unheard = unheardNote(report);
  if (unheard) block.appendChild(unheard);

  return block;
}

const positionLabelFor = (slice: BreakdownSlice): string =>
  slice.key === "for" ? positionLabel("For")
    : slice.key === "against" ? positionLabel("Against")
      : positionLabel("Neutral");

const channelLabelFor = (slice: BreakdownSlice): string =>
  slice.key === "phone" ? channelLabel("Phone") : channelLabel("InPerson");

const completionLabelFor = (slice: BreakdownSlice): string =>
  slice.key === "spoke" ? outcomeLabel("Spoke") : outcomeLabel("NoShow");

function breakdownPanel(
  title: string,
  slices: BreakdownSlice[],
  label: (slice: BreakdownSlice) => string,
  coverage?: string
): HTMLElement {
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);

  return el("div", { class: "spr-panel", "data-breakdown": title.toLowerCase() }, [
    el("h3", { class: "spr-panel-title", text: title }),
    el("ul", { class: "spr-panel-list" },
      slices.map((slice) => {
        const tip = barTip(label(slice), slice.count, total);
        return el("li", { class: "spr-panel-row", "data-slice-key": slice.key }, [
          el("span", { class: "spr-panel-k", text: label(slice) }),
          el("span", { class: "spr-panel-v", text: integer(slice.count) }),
          el("span", {
            class: "spr-panel-bar spr-bar-group",
            role: "img",
            "aria-label": tip,
            title: tip,
            style: `--spr-fill:${total === 0 ? 0 : Math.round((slice.count / total) * 100)}%`
          })
        ]);
      })),
    coverage
      ? el("p", {
        class: "spr-fine",
        "data-testid": "speaker-report-channel-coverage",
        text: coverage
      })
      : null
  ].filter(Boolean) as HTMLElement[]);
}

export function timeProfile(report: SpeakerParticipationReport): HTMLElement | null {
  const profile = report.timeProfile;

  // CityzenWeb and ApiCore ship on separate pipelines, so a bundle newer than the API it is
  // talking to is a normal state during a rollout. `timeline` is new in this release and
  // arrives undefined against an older API -- read it as optional at the point of use, the
  // same way `dashboards` is.
  const timeline = profile.timeline as Timeline | undefined;
  const hasTimeline = report.availability.timeline === true && !!timeline;

  // Nothing was timed and nothing is on the clock. The panel is absent rather than a row of
  // zeroes that look like measurements.
  if (profile.timedSignUps === 0 && !hasTimeline) return null;

  // The insight's ROOT, and the element the interval control publishes its applied state on.
  // Three sections rather than one: the stat tiles answer "how long", the two time blocks
  // answer "when", and a reader scanning for one should not have to read past the other.
  const root = el("div", {
    class: "spr-timeprofile",
    "data-testid": "speaker-report-time-profile"
  });

  if (profile.timedSignUps > 0) {
    root.appendChild(timeProfileStats(profile));
  }

  if (hasTimeline) {
    for (const block of timelineSections({ report, timeline: timeline as Timeline, root })) {
      root.appendChild(block);
    }
  }

  return root;
}

function timeProfileStats(profile: TimeProfile): HTMLElement {
  return el("section", { class: "spr-block", "aria-labelledby": "spr-time-h" }, [
    el("div", { class: "spr-block-head" }, [
      el("h2", { id: "spr-time-h", text: "Time profile" }),
      el("p", {
        class: "spr-sub",
        text: `Over the ${integer(profile.timedSignUps)} turns the clerk timed. The rest are not counted here.`
      })
    ]),
    el("div", { class: "spr-stats" }, [
      statTile("timeMean", "Mean", duration(profile.meanSeconds), "per timed turn"),
      statTile("timeMedian", "Median", duration(profile.medianSeconds), "half were shorter"),
      statTile("timeLongest", "Longest", duration(profile.longestSeconds), "single turn"),
      statTile(
        "timePercentOfAllotment",
        "Of allotment",
        profile.percentOfAllotment === null ? "n/a" : profile.percentOfAllotment + "%",
        profile.percentOfAllotment === null ? "no allotment set" : "mean against mean"),
      // A different statistic from the one above, and they routinely disagree: give a
      // two-minute and a six-minute speaker four minutes each and "of allotment" reads
      // 100% while this reads 50%. The note states the threshold the SERVER used rather
      // than a number hard-coded here, because it is configurable.
      statTile(
        "timeAllotmentHitRate",
        "Hit the limit",
        profile.allotmentHitRatePercent === null ? "n/a" : profile.allotmentHitRatePercent + "%",
        profile.allotmentHitRatePercent === null
          ? "no allotment set"
          : `of ${profile.timedSignUps} timed ${profile.timedSignUps === 1 ? "turn" : "turns"}, `
            + `reached ${profile.allotmentHitRateThresholdPercent}% of their time`)
    ])
  ]);
}
